<script setup lang="ts">
import Container from '@/components/www/primitives/Container.vue'
import {featureMenuItems} from './featureMenu'

/**
 * Desktop Features mega menu panel (ported from osello.com).
 * Visibility is driven by the parent Header via `open` — the panel handles its
 * own open/close animation through [data-open] + hidden.
 */
defineProps<{
    open: boolean
    hiddenPanel: boolean
}>()

const emit = defineEmits<{
    'link-click': []
}>()
</script>

<template>
  <div
      id="features-mega-menu"
      class="features-mega"
      :data-open="open ? 'true' : 'false'"
      :hidden="hiddenPanel"
      role="region"
      aria-label="Features"
  >
    <div class="features-mega__glow" aria-hidden="true"/>
    <Container width="lg" class="features-mega__inner">
      <div class="features-mega__layout">
        <aside class="features-mega__intro">
          <p class="features-mega__tagline">List. Format. Sync.<br>Expand.</p>
          <RouterLink class="features-mega__all" to="/features" @click="emit('link-click')">
            Explore all features
            <span class="features-mega__all-arrow" aria-hidden="true">&rarr;</span>
          </RouterLink>
        </aside>

        <ul class="features-mega__grid" role="list">
          <li
              v-for="(item, index) in featureMenuItems"
              :key="item.id"
              class="features-mega__item"
              :data-accent="item.accent"
              :style="{'--mega-i': index}"
          >
            <RouterLink class="features-mega__link" :to="item.to" @click="emit('link-click')">
              <span class="features-mega__icon" aria-hidden="true">
                <svg v-if="item.id === 'list'" viewBox="0 0 24 24" fill="none"><path d="M5 7h14M5 12h10M5 17h12" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/><path d="M18 11l2 1.5-2 1.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <svg v-else-if="item.id === 'optimize'" viewBox="0 0 24 24" fill="none"><path d="M12 3l1.4 4.3H18l-3.6 2.6 1.4 4.3L12 11.6 8.2 14.2l1.4-4.3L6 7.3h4.6L12 3z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
                <svg v-else-if="item.id === 'syndicate'" viewBox="0 0 24 24" fill="none"><path d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm10 14a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM7 20a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" stroke="currentColor" stroke-width="1.5"/><path d="M9.6 7.4l4.8 2.8M9.6 16.6l4.8-2.8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                <svg v-else viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.5"/><path d="M12 8v8M8 12h8" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/><path d="M4 4l3 3M20 4l-3 3M4 20l3-3M20 20l-3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              </span>
              <span class="features-mega__title">
                {{ item.titleLine1 }}
                <br>
                <em>{{ item.titleLine2 }}</em>
              </span>
              <span class="features-mega__desc">{{ item.description }}</span>
              <span class="features-mega__cta">
                Learn more
                <span class="features-mega__cta-arrow" aria-hidden="true">&rarr;</span>
              </span>
            </RouterLink>
          </li>
        </ul>
      </div>
    </Container>
  </div>
</template>

<style scoped>
.features-mega {
    --mega-ease: var(--ease-out-strong);
    position: absolute;
    inset-inline: 0;
    inset-block-start: 100%;
    z-index: 45;
    width: 100%;
    overflow: hidden;
    border-block-end: 1px solid var(--border);
    box-shadow:
        0 28px 72px -36px rgba(26, 24, 20, 0.12),
        0 12px 24px -16px rgba(26, 24, 20, 0.08);
    background:
        radial-gradient(ellipse 70% 55% at 8% -10%, rgba(26, 24, 20, 0.03), transparent 58%),
        radial-gradient(ellipse 45% 40% at 92% 110%, rgba(26, 24, 20, 0.04), transparent 55%),
        linear-gradient(180deg, var(--surface) 0%, var(--card-bg) 52%, var(--background) 100%);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-6px) scale(0.98);
    transform-origin: top center;
    transition:
        opacity 240ms var(--mega-ease),
        transform 260ms var(--mega-ease),
        visibility 240ms;
}

.features-mega__glow {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
        linear-gradient(115deg, transparent 40%, rgba(255, 255, 255, 0.55) 50%, transparent 60%);
    opacity: 0;
    transform: translateX(-30%);
    transition: opacity 0.28s var(--ease-out-strong);
}

.features-mega[data-open="true"] {
    z-index: 46;
    opacity: 1;
    visibility: visible;
    transform: translateY(0) scale(1);
}

.features-mega[data-open="true"] .features-mega__glow {
    opacity: 0.35;
    animation: features-mega-shimmer 1.1s var(--mega-ease) 0.08s both;
}

@keyframes features-mega-shimmer {
    from { transform: translateX(-35%); }
    to { transform: translateX(35%); }
}

.features-mega :deep(.features-mega__inner) {
    position: relative;
    z-index: 1;
    padding-block: calc(var(--space-10) + 20px) calc(var(--space-12) + 20px);
}

.features-mega__layout {
    display: grid;
    grid-template-columns: minmax(220px, 0.72fr) minmax(0, 2.4fr);
    gap: var(--space-10);
    align-items: stretch;
}

.features-mega__intro {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-inline-end: var(--space-7);
    border-inline-end: 1px solid rgba(26, 24, 20, 0.08);
}

.features-mega__tagline {
    margin: 0 0 var(--space-7);
    font-family: var(--font-heading);
    font-weight: var(--fw-medium);
    font-size: clamp(26px, 2.2vw, 34px);
    line-height: 1.08;
    letter-spacing: -0.03em;
    color: var(--secondary-500);
}

.features-mega__all {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    width: fit-content;
    font-family: var(--font-body);
    font-size: var(--type-body-md-size);
    font-weight: 600;
    color: var(--primary-500);
    text-decoration: none;
    transition: gap 0.25s var(--mega-ease), color 0.2s ease;
}

.features-mega__all:hover,
.features-mega__all:focus-visible {
    color: var(--secondary-500);
    gap: 0.75rem;
}

.features-mega__all:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 4px;
    border-radius: 4px;
}

.features-mega__all-arrow {
    display: inline-block;
    transition: transform 0.25s var(--mega-ease);
}

.features-mega__all:hover .features-mega__all-arrow {
    transform: translateX(3px);
}

.features-mega__grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--space-5);
    margin: 0;
    list-style: none;
    padding: 0;
    align-items: stretch;
}

.features-mega__item {
    --item-accent: var(--pillar-list);
    --item-accent-soft: rgba(26, 24, 20, 0.035);
    --item-accent-glow: rgba(0, 112, 96, 0.18);
    display: flex;
    min-height: 0;
}

.features-mega__item[data-accent="list"] {
    --item-accent: var(--pillar-list);
    --item-accent-soft: rgba(26, 24, 20, 0.035);
    --item-accent-glow: rgba(0, 112, 96, 0.2);
}

.features-mega__item[data-accent="optimize"] {
    --item-accent: var(--pillar-optimize);
    --item-accent-soft: rgba(26, 24, 20, 0.035);
    --item-accent-glow: rgba(71, 152, 141, 0.2);
}

.features-mega__item[data-accent="syndicate"] {
    --item-accent: var(--pillar-syndicate);
    --item-accent-soft: rgba(26, 24, 20, 0.035);
    --item-accent-glow: rgba(79, 143, 122, 0.18);
}

.features-mega__item[data-accent="expand"] {
    --item-accent: var(--pillar-expand);
    --item-accent-soft: rgba(26, 24, 20, 0.04);
    --item-accent-glow: rgba(1, 44, 36, 0.14);
}

.features-mega[data-open="true"] .features-mega__item {
    animation: features-mega-item-in 0.48s var(--mega-ease) both;
    animation-delay: calc(0.05s + var(--mega-i) * 0.055s);
}

@keyframes features-mega-item-in {
    from {
        opacity: 0;
        transform: translateY(14px);
    }
    to {
        opacity: 1;
        transform: none;
    }
}

.features-mega__link {
    position: relative;
    display: grid;
    grid-template-rows: calc(1.8rem + 30px) auto 1fr auto;
    align-content: start;
    width: 100%;
    height: 100%;
    min-height: 15.5rem;
    padding: var(--space-7);
    border-radius: var(--radius-xl);
    border: 0;
    background:
        linear-gradient(165deg, #fff 0%, var(--item-accent-soft) 100%);
    color: inherit;
    text-decoration: none;
    overflow: hidden;
    transition:
        transform 320ms var(--mega-ease);
}

@media (hover: hover) and (pointer: fine) {
    .features-mega__link:hover,
    .features-mega__link:focus-visible {
        transform: translateY(-3px);
    }
}

.features-mega__link:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 3px;
}

.features-mega__icon {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    width: 1.8rem;
    height: 1.8rem;
    flex-shrink: 0;
    color: var(--item-accent);
}

.features-mega__icon svg {
    width: 1.8rem;
    height: 1.8rem;
}

.features-mega__title {
    position: relative;
    z-index: 1;
    display: block;
    margin-block-end: var(--space-4);
    font-family: var(--font-heading);
    font-weight: var(--fw-medium);
    font-size: clamp(22px, 1.65vw, 28px);
    line-height: 1.08;
    letter-spacing: -0.025em;
    color: var(--secondary-500);
    transition: color 220ms var(--ease-out);
}

.features-mega__title em {
    font-style: normal;
    color: inherit;
}

.features-mega__desc {
    position: relative;
    z-index: 1;
    display: block;
    flex: 1;
    font-family: var(--font-body);
    font-size: var(--type-body-sm-size, 14px);
    line-height: 1.6;
    color: var(--text-secondary);
}

.features-mega__cta {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin-block-start: var(--space-5);
    font-size: 13px;
    font-weight: 600;
    color: var(--item-accent);
    opacity: 0;
    transform: translateY(6px);
    transition:
        opacity 250ms var(--mega-ease),
        transform 250ms var(--mega-ease);
}

.features-mega__link:hover .features-mega__cta,
.features-mega__link:focus-visible .features-mega__cta {
    opacity: 1;
    transform: none;
}

.features-mega__cta-arrow {
    transition: transform 0.25s var(--mega-ease);
}

.features-mega__link:hover .features-mega__cta-arrow,
.features-mega__link:focus-visible .features-mega__cta-arrow {
    transform: translateX(2px);
}

@media (max-width: 1199px) {
    .features-mega__layout {
        grid-template-columns: 1fr;
        gap: var(--space-8);
    }

    .features-mega__intro {
        padding-inline-end: 0;
        border-inline-end: 0;
        padding-block-end: var(--space-7);
        border-block-end: 1px solid rgba(1, 44, 36, 0.08);
    }

    .features-mega__grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 767px) {
    .features-mega {
        display: none;
    }
}

@media (prefers-reduced-motion: reduce) {
    .features-mega,
    .features-mega__item,
    .features-mega__glow {
        transition: none;
        animation: none !important;
        transform: none;
    }

    .features-mega__item {
        opacity: 1;
    }

    .features-mega__cta {
        opacity: 1;
        transform: none;
    }
}
</style>
